import React, { useMemo, useState } from 'react';
import { useProducts } from '../ProductContext';
import { productUnitLabel } from '../constants/productUnits';
import { isLowStock } from '../utils/lowStock';
import './AutomotiveInventoryPage.css';

function matchesSearch(p, q) {
  if (!q) return true;
  const s = q.trim().toLowerCase();
  if (!s) return true;
  const hay = [
    p.name,
    p.category,
    p.partCode,
    p.oemNumber,
    p.vehicleType,
    p.compatibleModels,
    p.brandName,
    p.supplierName,
    p.quantity,
    p.purchasingPrice,
    p.sellingPrice,
  ]
    .join(' ')
    .toLowerCase();
  return hay.includes(s);
}

function stockStatus(item) {
  const qty = Number(item.quantity) || 0;
  if (qty <= 0) return { key: 'out', label: 'No stock' };
  if (isLowStock(item)) return { key: 'low', label: 'Low stock' };
  return { key: 'ok', label: 'In stock' };
}

export default function AutomotiveInventoryPage({ onNavigate }) {
  const { products, inventoryLoading, refreshProducts } = useProducts();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');

  const fmt = (n) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString() : '—');
  const fmtMoney = (n) => (Number.isFinite(Number(n)) && Number(n) > 0 ? fmt(n) : '—');

  const inventoryItems = useMemo(() => {
    return (Array.isArray(products) ? products : [])
      .filter((p) => String(p?.approvalStatus || 'approved') === 'approved')
      .filter((p) => (Number(p.quantity) || 0) > 0)
      .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base' }));
  }, [products]);

  const categories = useMemo(() => {
    const set = new Set();
    inventoryItems.forEach((p) => {
      const c = String(p.category || '').trim();
      if (c) set.add(c);
    });
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [inventoryItems]);

  const filtered = useMemo(() => {
    return inventoryItems.filter((p) => {
      if (category !== 'all' && String(p.category || '').trim() !== category) return false;
      return matchesSearch(p, search);
    });
  }, [inventoryItems, category, search]);

  const stats = useMemo(() => {
    const low = inventoryItems.filter(isLowStock);
    const totalUnits = inventoryItems.reduce((sum, p) => sum + (Number(p.quantity) || 0), 0);
    const costValue = inventoryItems.reduce((sum, p) => {
      const qty = Number(p.quantity) || 0;
      const cost = Number(p.purchasingPrice) || 0;
      return sum + qty * cost;
    }, 0);
    const retailValue = inventoryItems.reduce((sum, p) => {
      const qty = Number(p.quantity) || 0;
      const sell = Number(p.sellingPrice) || 0;
      return sum + qty * sell;
    }, 0);
    return {
      itemCount: inventoryItems.length,
      totalUnits,
      lowCount: low.length,
      costValue,
      retailValue,
    };
  }, [inventoryItems]);

  if (inventoryLoading && inventoryItems.length === 0) {
    return (
      <div className="auto-inventory page-bleed">
        <p className="auto-inventory__muted">Loading inventory…</p>
      </div>
    );
  }

  return (
    <div className="auto-inventory page-bleed">
      <header className="auto-inventory__head">
        <div>
          <h1>Inventory</h1>
          <p>Only parts with physical stock in your shop appear here — after opening stock or receiving a purchase.</p>
        </div>
        <button type="button" className="auto-inventory__secondary" onClick={() => refreshProducts()} disabled={inventoryLoading}>
          {inventoryLoading ? 'Loading…' : 'Refresh'}
        </button>
      </header>

      <div className="auto-inventory__stats">
        <article className="auto-inventory__stat">
          <span>Parts in shop</span>
          <strong>{stats.itemCount}</strong>
        </article>
        <article className="auto-inventory__stat">
          <span>Total units</span>
          <strong>{fmt(stats.totalUnits)}</strong>
        </article>
        <article className="auto-inventory__stat">
          <span>Stock value (cost)</span>
          <strong>{stats.costValue > 0 ? `TZS ${fmt(stats.costValue)}` : '—'}</strong>
        </article>
        <article className="auto-inventory__stat">
          <span>Retail value</span>
          <strong>{stats.retailValue > 0 ? `TZS ${fmt(stats.retailValue)}` : '—'}</strong>
        </article>
        <article className="auto-inventory__stat auto-inventory__stat--warn">
          <span>Low stock</span>
          <strong>{stats.lowCount}</strong>
        </article>
      </div>

      <section className="auto-inventory__panel">
        <div className="auto-inventory__toolbar">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, category, qty, price…"
          />
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="all">All categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          {(search.trim() || category !== 'all') && (
            <button type="button" className="auto-inventory__secondary" onClick={() => { setSearch(''); setCategory('all'); }}>
              Reset
            </button>
          )}
        </div>

        <p className="auto-inventory__count">
          Showing <strong>{filtered.length}</strong> of {inventoryItems.length} stocked parts
        </p>

        {inventoryItems.length === 0 ? (
          <div className="auto-inventory__empty">
            <p>No stock in the shop yet. Register a spare part first, then add opening stock or receive a purchase.</p>
            {typeof onNavigate === 'function' ? (
              <div className="auto-inventory__hint-actions">
                <button type="button" className="auto-inventory__primary" onClick={() => onNavigate('auto-opening-stock')}>
                  Opening stock
                </button>
                <button type="button" className="auto-inventory__secondary" onClick={() => onNavigate('orders-tracking')}>
                  Receive purchase
                </button>
              </div>
            ) : null}
          </div>
        ) : filtered.length === 0 ? (
          <p className="auto-inventory__muted">No items match your filters.</p>
        ) : (
          <div className="auto-inventory__table-wrap">
            <table className="auto-inventory__table">
              <thead>
                <tr>
                  <th>Part</th>
                  <th>Category</th>
                  <th>Unit</th>
                  <th>Qty</th>
                  <th>Buy</th>
                  <th>Sell</th>
                  <th>Value</th>
                  <th>Alert</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const qty = Number(p.quantity) || 0;
                  const buy = Number(p.purchasingPrice) || 0;
                  const sell = Number(p.sellingPrice) || 0;
                  const lineValue = qty * buy;
                  const status = stockStatus(p);
                  return (
                    <tr key={p.id} data-status={status.key}>
                      <td>
                        <strong>{p.name}</strong>
                        <span>
                          {[
                            p.partCode ? `SKU: ${p.partCode}` : '',
                            p.oemNumber ? `OEM: ${p.oemNumber}` : '',
                            p.brandName || '',
                          ].filter(Boolean).join(' • ') || 'Spare part'}
                        </span>
                        {p.compatibleModels ? <span>Fits: {p.compatibleModels}</span> : null}
                        {p.supplierName ? <span>Supplier: {p.supplierName}</span> : null}
                      </td>
                      <td>{p.category || '—'}</td>
                      <td>{productUnitLabel(p.unit)}</td>
                      <td className="auto-inventory__num">{fmt(qty)}</td>
                      <td className="auto-inventory__num">{fmtMoney(buy)}</td>
                      <td className="auto-inventory__num">{fmtMoney(sell)}</td>
                      <td className="auto-inventory__num">{lineValue > 0 ? fmt(lineValue) : '—'}</td>
                      <td className="auto-inventory__num">{Number(p.lowStockLimit) > 0 ? fmt(p.lowStockLimit) : '—'}</td>
                      <td>
                        <span className={`auto-inventory__badge auto-inventory__badge--${status.key}`}>{status.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

    </div>
  );
}
